import { has, head, isUndefined, matches, propOr, sumBy, update } from 'lodash/fp';

import { formatMinorUnits, parseMinorUnits } from './apiUtils';
import { isNumeric } from './numberUtils';
import type { Amount } from '../@types/amount';
import type { Currency } from '../@types/currency';

export const CURRENCY_CZK = 'CZK';
export const CURRENCY_EUR = 'EUR';
export const DEFAULT_CURRENCY = CURRENCY_CZK;

export const nullAmount: Amount = { amount: 0, currency: DEFAULT_CURRENCY };

export function divideAmount(amount: Amount, denominator: number): Amount {
	return {
		...amount,
		amount: amount.amount / denominator,
	};
}

/**
 * Given list of amounts, returns its sum. If provided with empty list, defaults
 * to `{ amount: 0, currency: DEFAULT_CURRENCY }`
 * @param {Amount[]} amounts
 * @return {Amount}
 */
export const sumAmounts = (amounts: Amount[]): Amount => {
	const currency = propOr(DEFAULT_CURRENCY, 'currency', head(amounts));

	console.assert(
		amounts.every(matches({ currency })),
		'Sum of different currencies produces false results.'
	);

	return {
		amount: sumBy('amount', amounts),
		currency,
	};
};

/**
 * Takes Amount and returns it with negative amount.
 * @param {Amount} amount
 * @return {Amount}
 */
export const toNegativeAmount = update('amount', (amount) => -Math.abs(amount));

/**
 * Takes Amount and returns its amount as number parsed from minor units.
 */
export const toFloat = (amount: Amount): number => parseMinorUnits(amount.amount);

/**
 * Converts Amount type to float
 * Used as redux-form/Field.format function
 * @param {Amount | undefined} storedValue react intl object
 * @return {number}
 */
export const formatAmount = (storedValue: Amount | undefined): number => {
	const amount = has('amount', storedValue) ? parseMinorUnits(storedValue?.amount) : storedValue;
	return !isUndefined(amount) ? amount : '';
};

/**
 * Converts float to Amount type
 * Used as redux-form/Field.parse function
 * @param {number} formattedValue
 * @param {boolean} unRounded
 * @return {{amount: number, currency: Currency}}
 */
export const parseAmount = (
	formattedValue: number | Amount,
	currency: Currency,
	unRounded = false
): Amount =>
	isNumeric(formattedValue)
		? {
				amount: formatMinorUnits(formattedValue, unRounded),
				currency,
			}
		: (formattedValue as Amount);

export const absoluteAmount = update(['amount'], Math.abs);
